/**
 * Form design theme — frontend contract + CSS-variable projection.
 *
 * Smart Forms carry no style data of their own; visual design comes from a
 * reusable, org-scoped "design template" (form_design_templates). The backend
 * resolves a fully-populated theme (services/form_theme.py resolve_form_theme)
 * and returns it on the public form payload and per design template.
 *
 * This module turns a (possibly partial) theme into `--wf-*` CSS variables that
 * FormRenderer's inputs/sections/buttons read, mirroring the app's existing
 * `--v2-*` token pattern. Keep DEFAULT_FORM_THEME in sync with the Python
 * DEFAULT_FORM_THEME in services/form_theme.py.
 */

export interface FormThemeColors {
  primary: string;
  accent: string;
  text: string;
  muted: string;
  background: string;
  surface: string;
  border: string;
  error: string;
  success: string;
  /** Amber. "Plan data, read-only", "due in 3 days" — a state that is neither an
   *  error nor a success. The ECM design (TRUS-571) leans on it heavily and the
   *  palette had no slot for it, so every such marker was rendered as an error. */
  warning: string;
}

export interface FormThemeTypography {
  font_family: string;
  heading_font: string;
  /** Tabular data that must line up — MIF production dates, field keys, scores.
   *  Empty falls back to the body face, so an org that sets nothing is unchanged. */
  mono_font: string;
  base_size: string;
  heading_weight: string;
}

export interface FormThemeControls {
  input_radius: string;
  input_border_style: string;
  input_bg: string;
  focus_ring: string;
  button_style: 'solid' | 'outline' | 'pill';
}

export interface FormThemeLayout {
  max_width: string;
  section_style: 'card' | 'flat' | 'divided';
  spacing: 'compact' | 'comfortable';
  label_position: 'top' | 'left';
}

export interface FormThemeBranding {
  logo_url: string;
  logo_dark_url: string;
  show_logo: boolean;
  show_powered_by: boolean;
}

export interface FormThemeBackground {
  type: 'solid' | 'gradient' | 'image';
  value: string;
}

export interface FormTheme {
  colors: FormThemeColors;
  typography: FormThemeTypography;
  controls: FormThemeControls;
  layout: FormThemeLayout;
  branding: FormThemeBranding;
  background: FormThemeBackground;
}

/** Deeply-partial theme, as delivered by the API (any subset of keys). */
export type PartialFormTheme = {
  [K in keyof FormTheme]?: Partial<FormTheme[K]>;
};

export const DEFAULT_FORM_THEME: FormTheme = {
  colors: {
    primary: '#2563EB',
    accent: '#2563EB',
    text: '#111827',
    muted: '#6B7280',
    background: '#F9FAFB',
    surface: '#FFFFFF',
    border: '#D1D5DB',
    error: '#DC2626',
    success: '#16A34A',
    warning: '#B45309',
  },
  typography: {
    font_family: 'Inter, system-ui, -apple-system, sans-serif',
    heading_font: '',
    mono_font: '',
    base_size: '14px',
    heading_weight: '600',
  },
  controls: {
    input_radius: '8px',
    input_border_style: 'solid',
    input_bg: '#FFFFFF',
    focus_ring: '',
    button_style: 'solid',
  },
  layout: {
    max_width: '672px',
    section_style: 'card',
    spacing: 'comfortable',
    label_position: 'top',
  },
  branding: {
    logo_url: '',
    logo_dark_url: '',
    show_logo: true,
    show_powered_by: true,
  },
  background: {
    type: 'solid',
    value: '',
  },
};

/** Merge a partial theme onto DEFAULT_FORM_THEME so every key is present. */
export function mergeFormTheme(theme?: PartialFormTheme | null): FormTheme {
  if (!theme) return DEFAULT_FORM_THEME;
  const groups = Object.keys(DEFAULT_FORM_THEME) as (keyof FormTheme)[];
  const out = {} as FormTheme;
  for (const g of groups) {
    // Drop null/undefined/'' overrides so a sparse theme never blanks a default.
    const incoming = (theme[g] || {}) as Record<string, any>;
    const cleaned: Record<string, any> = {};
    for (const [k, v] of Object.entries(incoming)) {
      if (v !== null && v !== undefined && v !== '') cleaned[k] = v;
    }
    out[g] = { ...(DEFAULT_FORM_THEME[g] as any), ...cleaned };
  }
  return out;
}

/**
 * Project a theme into `--wf-*` CSS variables. Apply to a wrapper element; the
 * renderer's inputs/labels/sections/buttons reference these vars. Also sets
 * font-family and base font-size so typography cascades.
 */
export function themeToCssVars(theme?: PartialFormTheme | null): React.CSSProperties {
  const t = mergeFormTheme(theme);
  const focusRing = t.controls.focus_ring || t.colors.primary;
  const headingFont = t.typography.heading_font || t.typography.font_family;
  const bg =
    t.background.type !== 'solid' && t.background.value ? t.background.value : t.colors.background;
  // Soft tints. The design fills cards and chips with a wash of the state colour
  // rather than the colour itself (--accent-soft, --warn-soft, --crit-soft,
  // --ok-soft in the preview). Deriving them with color-mix keeps a theme to one
  // value per state instead of eight, and degrades to the solid colour in a
  // browser without it.
  const tint = (c: string) => `color-mix(in srgb, ${c} 12%, transparent)`;
  return {
    // colors
    ['--wf-primary' as any]: t.colors.primary,
    ['--wf-accent' as any]: t.colors.accent,
    ['--wf-text' as any]: t.colors.text,
    ['--wf-muted' as any]: t.colors.muted,
    ['--wf-bg' as any]: bg,
    ['--wf-surface' as any]: t.colors.surface,
    ['--wf-border' as any]: t.colors.border,
    ['--wf-error' as any]: t.colors.error,
    ['--wf-success' as any]: t.colors.success,
    ['--wf-warning' as any]: t.colors.warning,
    ['--wf-accent-soft' as any]: tint(t.colors.accent),
    ['--wf-error-soft' as any]: tint(t.colors.error),
    ['--wf-success-soft' as any]: tint(t.colors.success),
    ['--wf-warning-soft' as any]: tint(t.colors.warning),
    // typography
    ['--wf-font' as any]: t.typography.font_family,
    ['--wf-heading-font' as any]: headingFont,
    ['--wf-heading-weight' as any]: t.typography.heading_weight,
    ['--wf-mono' as any]: t.typography.mono_font || t.typography.font_family,
    // layout (TRUS-545 Phase 2). These three keys have been in the theme
    // contract, and in the design-template editor, since design templates
    // shipped — and were read by nothing. `spacing` was a live admin dropdown
    // that did not move a pixel. They are projected here so the renderer can
    // actually honor them.
    ['--wf-max-width' as any]: t.layout.max_width,
    ['--wf-row-gap' as any]: t.layout.spacing === 'compact' ? '0.5rem' : '1rem',
    ['--wf-col-gap' as any]: t.layout.spacing === 'compact' ? '0.75rem' : '1rem',
    // controls
    ['--wf-radius' as any]: t.controls.input_radius,
    ['--wf-input-bg' as any]: t.controls.input_bg,
    ['--wf-input-border-style' as any]: t.controls.input_border_style,
    ['--wf-focus' as any]: focusRing,
    // base cascade
    fontFamily: t.typography.font_family,
    fontSize: t.typography.base_size,
    color: t.colors.text,
  };
}

/**
 * Curated background catalog — gradients and subtle patterns that need no
 * external hosting (pure CSS / inline SVG data-URIs), plus a "solid" and an
 * "upload" affordance handled by the editor. `value` is a CSS background value.
 */
export interface BackgroundPreset {
  id: string;
  label: string;
  type: 'solid' | 'gradient' | 'image';
  /** CSS value: a color, a gradient(), or url(...) for image presets. */
  value: string;
  /** Thumbnail CSS (same as value for gradients). */
  swatch: string;
}

export const BACKGROUND_PRESETS: BackgroundPreset[] = [
  { id: 'none', label: 'Solid', type: 'solid', value: '', swatch: '#F9FAFB' },
  {
    id: 'aurora',
    label: 'Aurora',
    type: 'gradient',
    value: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    swatch: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  },
  {
    id: 'sunrise',
    label: 'Sunrise',
    type: 'gradient',
    value: 'linear-gradient(135deg, #ff9a9e 0%, #fad0c4 99%)',
    swatch: 'linear-gradient(135deg, #ff9a9e 0%, #fad0c4 99%)',
  },
  {
    id: 'ocean',
    label: 'Ocean',
    type: 'gradient',
    value: 'linear-gradient(120deg, #a1c4fd 0%, #c2e9fb 100%)',
    swatch: 'linear-gradient(120deg, #a1c4fd 0%, #c2e9fb 100%)',
  },
  {
    id: 'mint',
    label: 'Mint',
    type: 'gradient',
    value: 'linear-gradient(135deg, #d4fc79 0%, #96e6a1 100%)',
    swatch: 'linear-gradient(135deg, #d4fc79 0%, #96e6a1 100%)',
  },
  {
    id: 'dusk',
    label: 'Dusk',
    type: 'gradient',
    value: 'linear-gradient(135deg, #2b5876 0%, #4e4376 100%)',
    swatch: 'linear-gradient(135deg, #2b5876 0%, #4e4376 100%)',
  },
  {
    id: 'blush',
    label: 'Blush',
    type: 'gradient',
    value: 'linear-gradient(135deg, #fdfbfb 0%, #ebedee 100%)',
    swatch: 'linear-gradient(135deg, #fdfbfb 0%, #ebedee 100%)',
  },
  {
    id: 'dots',
    label: 'Soft dots',
    type: 'image',
    value:
      'radial-gradient(circle, rgba(99,102,241,0.18) 1px, transparent 1px) 0 0 / 18px 18px, #F8FAFF',
    swatch: 'radial-gradient(circle, #6366f1 1.5px, #F8FAFF 1.5px) 0 0 / 10px 10px',
  },
  {
    id: 'grid',
    label: 'Grid',
    type: 'image',
    value:
      'linear-gradient(rgba(0,0,0,0.05) 1px, transparent 1px) 0 0 / 22px 22px, linear-gradient(90deg, rgba(0,0,0,0.05) 1px, transparent 1px) 0 0 / 22px 22px, #FFFFFF',
    swatch: 'linear-gradient(#e5e7eb 1px, #fff 1px) 0 0 / 8px 8px, linear-gradient(90deg, #e5e7eb 1px, #fff 1px) 0 0 / 8px 8px',
  },
];

/**
 * Page-level background style for the public form container. Handles the three
 * background types: solid color, CSS gradient/pattern, or an uploaded image URL.
 */
export function themePageBackgroundStyle(theme?: PartialFormTheme | null): React.CSSProperties {
  const t = mergeFormTheme(theme);
  const { type, value } = t.background;
  if (type === 'image' && value) {
    // An uploaded image URL vs a CSS pattern/gradient string.
    const isUrl = /^https?:\/\//i.test(value) || value.startsWith('data:');
    if (isUrl) {
      return {
        backgroundImage: `url("${value}")`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
      };
    }
    // CSS pattern preset (e.g. the dots/grid presets).
    return { background: value };
  }
  if (type === 'gradient' && value) {
    return { backgroundImage: value };
  }
  return { backgroundColor: t.colors.background };
}

/** Inline style for a primary button honoring the theme's button_style. */
export function themeButtonStyle(theme?: PartialFormTheme | null): React.CSSProperties {
  const t = mergeFormTheme(theme);
  if (t.controls.button_style === 'outline') {
    return {
      backgroundColor: 'transparent',
      color: t.colors.primary,
      border: `1px solid ${t.colors.primary}`,
      borderRadius: t.controls.input_radius,
    };
  }
  return {
    backgroundColor: t.colors.primary,
    color: '#ffffff',
    border: '1px solid transparent',
    borderRadius: t.controls.button_style === 'pill' ? '9999px' : t.controls.input_radius,
  };
}

/** Whether a resolved theme is present (any non-empty group), for gating. */
export function hasTheme(theme?: PartialFormTheme | null): boolean {
  return !!theme && Object.keys(theme).length > 0;
}
